// Spektrix Authentication utilities
import { spektrixApiUrl } from './config'

class SpektrixAuth {
  constructor() {
    this.currentUser = null
    this.isAuthenticated = false
    this.authCheckPromise = null
  }

  // Check if user is currently authenticated
  async checkAuthStatus() {
    // Return existing promise if already checking
    if (this.authCheckPromise) {
      return this.authCheckPromise
    }

    this.authCheckPromise = this._performAuthCheck()
    return this.authCheckPromise
  }

  async _performAuthCheck() {
    try {
      const response = await fetch(`${spektrixApiUrl}/customer?$expand=subscriptions`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const userData = await response.json()
        this.currentUser = userData
        this.isAuthenticated = true
        this._notifyAuthChange()
        return { authenticated: true, user: userData }
      } else if (response.status === 401) {
        // User is not authenticated - this is expected
        this.currentUser = null
        this.isAuthenticated = false
        this._notifyAuthChange()
        return { authenticated: false, user: null }
      } else {
        // Other errors
        console.error('Auth check returned unexpected status:', response.status)
        this.currentUser = null
        this.isAuthenticated = false
        this._notifyAuthChange()
        return { authenticated: false, user: null }
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      this.currentUser = null
      this.isAuthenticated = false
      this._notifyAuthChange()
      return { authenticated: false, user: null }
    } finally {
      this.authCheckPromise = null
    }
  }

  // Authenticate user with email and password
  async login(email, password) {
    try {
      const response = await fetch(`${spektrixApiUrl}/customer/authenticate`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          password: password
        })
      })

      if (response.ok) {
        // Authentication successful, now fetch full customer data
        try {
          const customerResponse = await fetch(`${spektrixApiUrl}/customer?$expand=subscriptions`, {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          })

          if (customerResponse.ok) {
            const fullUserData = await customerResponse.json()
            this.currentUser = fullUserData
            this.isAuthenticated = true
            this._notifyAuthChange()
            return { success: true, user: fullUserData }
          } else {
            // Fallback to basic auth response if customer fetch fails
            const basicUserData = await response.json()
            this.currentUser = basicUserData
            this.isAuthenticated = true
            this._notifyAuthChange()
            return { success: true, user: basicUserData }
          }
        } catch (customerError) {
          console.warn('Failed to fetch full customer data, using basic auth response:', customerError)
          // Fallback to basic auth response
          const basicUserData = await response.json()
          this.currentUser = basicUserData
          this.isAuthenticated = true
          this._notifyAuthChange()
          return { success: true, user: basicUserData }
        }
      } else {
        const errorData = await response.json().catch(() => ({}))
        return {
          success: false,
          error: errorData.message || 'Login failed. Please check your credentials.'
        }
      }
    } catch (error) {
      console.error('Login failed:', error)
      return {
        success: false,
        error: 'Network error. Please try again.'
      }
    }
  }

  // Logout user
  async logout() {
    try {
      // Call deauthenticate endpoint
      await fetch(`${spektrixApiUrl}/customer/deauthenticate`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      }).catch(() => {
        // Ignore errors - deauthenticate endpoint might not exist
      })
    } catch (error) {
      console.error('Logout request failed:', error)
    }

    // Clear local state regardless
    this.currentUser = null
    this.isAuthenticated = false
    this._notifyAuthChange()
  }

  // Get current user info
  getCurrentUser() {
    return this.currentUser
  }

  // Check if user is authenticated
  getIsAuthenticated() {
    return this.isAuthenticated
  }

  // Notify components of auth state changes
  _notifyAuthChange() {
    window.dispatchEvent(new CustomEvent('spektrix-auth-change', {
      detail: {
        authenticated: this.isAuthenticated,
        user: this.currentUser
      }
    }))
  }

  // Add auth change listener
  onAuthChange(callback) {
    const handler = (event) => callback(event.detail)
    window.addEventListener('spektrix-auth-change', handler)

    // Return cleanup function
    return () => window.removeEventListener('spektrix-auth-change', handler)
  }
}

// Create singleton instance
const spektrixAuth = new SpektrixAuth()

export default spektrixAuth
